import { Flex, Grid } from "@eworkit/iris";
import { FormikHelpers } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { IEditableSectionProps } from "@/components/EditableSection";
import { Header } from "@/components/Header";
import { AddressType, createAddress, fetchAddress, IAddress, putAddress } from "@/data/companies";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";
import { CompanyAddressPreview } from "@/views/Settings/CompanySettings/CompanyContactDetails/CompanyAddress/CompanyAddressPreview";
import { CompanyAddressEdit } from "@/views/Settings/CompanySettings/CompanyContactDetails/CompanyAddress/CompanyAddressSectionEdit";

interface ICompanyAddressSectionProps {
  editableProps: IEditableSectionProps;
}

const getAddressInitialValues = (type: string): IAddress => ({
  type,
  addressLine1: "",
  addressLine2: "",
  zipCode: "",
  state: "",
  city: {
    city: "",
    country: "",
    countryCode: "",
    suggestedPhoneCode: null,
    name: "",
    locationId: "",
    coordinates: undefined,
  },
});

export const CompanyAddressSection = ({ editableProps }: ICompanyAddressSectionProps) => {
  const Company = useCompanyContext();
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const { data: fetchedAddresses, refetch } = useQuery(
    `/api/companies/company-address/${Company.id}`,
    () => fetchAddress(Company.id),
    {
      enabled: !!Company.id,
    },
  );

  const addresses = useMemo(() => {
    const companyAddresses = fetchedAddresses || [];

    return Object.keys(AddressType).map(
      type => companyAddresses.find(address => address.type === type) || getAddressInitialValues(type),
    );
  }, [fetchedAddresses]);

  const handleSubmit = async (values: IAddress[], actions: FormikHelpers<IAddress[]>) => {
    const action = fetchedAddresses?.length ? "update" : "creation";

    try {
      action === "update" ? await putAddress(values) : await createAddress(Company.id, values);

      actions.setSubmitting(false);
      editableProps.handleCancelClick();
      await refetch();

      addToast({
        text: t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.TOAST.SUCCESS", { action }),
        type: "success",
      });
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.TOAST.FAILURE", { action }),
        type: "danger",
      });
    }
  };

  return (
    <>
      <Flex mb="lg">
        <Header size="xs" text={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.HEADER")} />
      </Flex>

      <Grid columns="1fr 1fr" gap="sm" mb="sm">
        <Header size="xs" text={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.POSTAL")} />
        <Header size="xs" text={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.INVOICE")} />
      </Grid>

      {editableProps.editMode ? (
        <CompanyAddressEdit
          source={Company}
          onSubmit={handleSubmit}
          initialValues={addresses}
          editableProps={editableProps}
          onCancel={editableProps.handleCancelClick}
        />
      ) : (
        <CompanyAddressPreview editableProps={editableProps} addresses={addresses}></CompanyAddressPreview>
      )}
    </>
  );
};
